import { BALANCE } from "@station-keeping/sim";
import { TILE_H, TILE_W, fromIso, stationBounds, toIso, type Bounds, type Point } from "./iso";

/** Au plus loin : toute la station tient dans la zone libre, avec ce recul en plus (part de l'échelle). */
const ZOOM_OUT_SLACK = 0.85;
/** Au plus près : la zone libre montre encore autant de cases dans chaque sens. */
const CLOSEST_CELLS = 3;
/** Marge autour des anneaux ouverts où la vue peut aller, en cases : les vaisseaux y attendent. */
const ROAM_MARGIN = 1.5;
/** Le centre de la vue ne s'éloigne pas plus du bord des anneaux ouverts, en cases. */
const CENTER_REACH = 0.5;

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Caméra 2D : l'écran voit le monde (en pixels d'art) décalé de (x, y) et agrandi de `scale`. Elle cadre
 * les anneaux ouverts dans la zone que l'interface laisse libre, et ne laisse ni trop s'approcher, ni trop
 * s'éloigner, ni perdre la station de vue. Fonctions pures de conversion, testables sans canevas.
 */
export class Camera {
  x = 0;
  y = 0;
  scale = 1;
  width = 1;
  height = 1;
  /** Marges de l'interface par-dessus le canevas (HUD en haut, tiroir en bas…) : voir `setInsets`. */
  insets: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
  /** Anneaux ouverts : le cadrage et les bornes s'élargissent avec la station. */
  rings: number = BALANCE.grid.startRings;
  /** Marge autour des anneaux ouverts, en cases : le prochain anneau s'y devine, les vaisseaux y attendent. */
  margin = 1.5;
  /**
   * Part de la largeur de la station qui doit tenir à l'écran : en portrait, les pointes du losange
   * peuvent déborder un peu, les modules gagnent en taille.
   */
  cropX = 1;
  /** Vrai tant que le joueur n'a pas déplacé la vue : elle suit alors le cadrage de la station. */
  follow = true;

  resize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
  }

  /**
   * Nouvelles marges de l'interface. Si le joueur a déplacé la vue, elle glisse avec le centre de la zone
   * libre : ce qu'il regardait reste au milieu, à côté du panneau qui s'ouvre plutôt que dessous. Sinon,
   * `ease` recadre toute la station.
   */
  setInsets(insets: Insets): void {
    const before = this.insets;
    this.insets = insets;
    if (this.follow) return;
    this.x += (insets.left - before.left - (insets.right - before.right)) / 2;
    this.y += (insets.top - before.top - (insets.bottom - before.bottom)) / 2;
    this.clamp();
  }

  bounds(): Bounds {
    return stationBounds(this.rings + this.margin);
  }

  /** Bornes du zoom pour cette station et cette zone libre. */
  zoomRange(): { min: number; max: number } {
    const free = this.free();
    const roam = stationBounds(this.rings + ROAM_MARGIN);
    const min = ZOOM_OUT_SLACK * Math.min(free.width / roam.width, free.height / roam.height);
    const max = Math.min(free.width / (CLOSEST_CELLS * TILE_W), free.height / (CLOSEST_CELLS * TILE_H));
    return { min, max: Math.max(min, max) };
  }

  /** Le cadrage de toute la station dans la zone visible. */
  fitTarget(): { x: number; y: number; scale: number } {
    const free = this.free();
    const bounds = this.bounds();
    const { min, max } = this.zoomRange();
    const scale = clamp(Math.min(free.width / (bounds.width * this.cropX), free.height / bounds.height), min, max);
    return {
      x: free.x + free.width / 2 - (bounds.x + bounds.width / 2) * scale,
      y: free.y + free.height / 2 - (bounds.y + bounds.height / 2) * scale,
      scale,
    };
  }

  /** Cadre toute la station dans la zone visible, et reprend le suivi. */
  fit(): void {
    const target = this.fitTarget();
    this.x = target.x;
    this.y = target.y;
    this.scale = target.scale;
    this.follow = true;
  }

  /**
   * Rapproche la vue du cadrage d'une fraction `amount` (0 à 1), si elle le suit encore : un panneau qui
   * s'ouvre décale la station en douceur au lieu de la cacher. Sinon, garde la vue dans ses bornes (la
   * fenêtre change de taille, la station s'agrandit).
   */
  ease(amount: number): void {
    if (!this.follow) {
      this.clamp();
      return;
    }
    const target = this.fitTarget();
    this.x += (target.x - this.x) * amount;
    this.y += (target.y - this.y) * amount;
    this.scale += (target.scale - this.scale) * amount;
  }

  screenToWorld(sx: number, sy: number): Point {
    return { x: (sx - this.x) / this.scale, y: (sy - this.y) / this.scale };
  }

  worldToScreen(wx: number, wy: number): Point {
    return { x: wx * this.scale + this.x, y: wy * this.scale + this.y };
  }

  pan(dx: number, dy: number): void {
    this.x += dx;
    this.y += dy;
    this.follow = false;
    this.clamp();
  }

  /** Zoom autour d'un point de l'écran, qui reste sous le doigt ou le curseur. */
  zoomAt(sx: number, sy: number, factor: number): void {
    const { min, max } = this.zoomRange();
    this.scaleAround(sx, sy, clamp(this.scale * factor, min, max));
    this.follow = false;
    this.clamp();
  }

  /** Ramène un point du monde dans la zone visible, avec une marge (déplacement au clavier). */
  ensureVisible(point: Point, margin: number): void {
    const screen = this.worldToScreen(point.x, point.y);
    const pad = margin * this.scale;
    const { x, y } = this;
    if (screen.x < this.insets.left + pad) this.x += this.insets.left + pad - screen.x;
    if (screen.x > this.width - this.insets.right - pad) this.x -= screen.x - (this.width - this.insets.right - pad);
    if (screen.y < this.insets.top + pad) this.y += this.insets.top + pad - screen.y;
    if (screen.y > this.height - this.insets.bottom - pad) this.y -= screen.y - (this.height - this.insets.bottom - pad);
    if (this.x !== x || this.y !== y) {
      this.follow = false;
      this.clamp();
    }
  }

  /** La zone que l'interface laisse libre, en pixels du canevas. */
  private free(): Bounds {
    return {
      x: this.insets.left,
      y: this.insets.top,
      width: Math.max(1, this.width - this.insets.left - this.insets.right),
      height: Math.max(1, this.height - this.insets.top - this.insets.bottom),
    };
  }

  private scaleAround(sx: number, sy: number, scale: number): void {
    const before = this.screenToWorld(sx, sy);
    this.scale = scale;
    this.x = sx - before.x * scale;
    this.y = sy - before.y * scale;
  }

  /**
   * Garde la vue dans ses bornes : un zoom permis, la station (marge comprise) entière tant qu'elle tient
   * dans la zone libre et la couvrant sinon, et le centre de la vue au-dessus des anneaux ouverts.
   */
  private clamp(): void {
    const free = this.free();
    const centerX = free.x + free.width / 2;
    const centerY = free.y + free.height / 2;
    const { min, max } = this.zoomRange();
    const scale = clamp(this.scale, min, max);
    if (scale !== this.scale) this.scaleAround(centerX, centerY, scale);

    const roam = stationBounds(this.rings + ROAM_MARGIN);
    this.x = clampAxis(this.x, roam.x, roam.width, free.x, free.width, this.scale);
    this.y = clampAxis(this.y, roam.y, roam.height, free.y, free.height, this.scale);

    // Dans le repère de la grille, les anneaux forment un carré : le centre de la vue y reste.
    const center = this.screenToWorld(centerX, centerY);
    const grid = fromIso(center.x, center.y);
    const reach = this.rings + CENTER_REACH;
    const gx = clamp(grid.x, -reach, reach);
    const gy = clamp(grid.y, -reach, reach);
    if (gx !== grid.x || gy !== grid.y) {
      const target = toIso(gx, gy);
      this.x = centerX - target.x * this.scale;
      this.y = centerY - target.y * this.scale;
    }
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Décalage permis sur un axe pour un intervalle du monde (`start`, `size`) : plus petit que la zone libre, il
 * y reste entier ; plus grand, il la couvre toute.
 */
function clampAxis(offset: number, start: number, size: number, freeStart: number, freeSize: number, scale: number): number {
  const startInside = freeStart - start * scale;
  const endInside = freeStart + freeSize - (start + size) * scale;
  return clamp(offset, Math.min(startInside, endInside), Math.max(startInside, endInside));
}
